import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

import { slugify } from '../../utils/helpers';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [settings, setSettings] = useState({});
  const [treatments, setTreatments] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();

  // Close menus when route changes
  useEffect(() => {
    setIsOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    axios.get(`${API}/api/public/settings`).then(res => {
      setPhone(res.data?.phone || '');
      setSettings(res.data || {});
    });
    axios.get(`${API}/api/public/treatments`).then(res => setTreatments(res.data || []));
  }, []);

  const navLinks = [
    { name: 'Ana Sayfa', href: '/' },
    { name: 'Öncesi Sonrası', href: '/#oncesi-sonrasi' },
    { name: 'Hekimlerimiz', href: '/#hekimlerimiz' },
    { name: 'Blog', href: '/#blog' },
    { name: 'İletişim', href: '/#iletisim' },
  ];

  const getTreatmentSlug = (t) => t.id ? t.id : slugify(t.title);

  return (
    <nav className="bg-white/95 backdrop-blur-md sticky top-0 z-50 border-b border-slate-100 shadow-sm relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex justify-between items-center h-24">
          <Link to="/">
            <img src="https://customer-assets.emergentagent.com/job_dentakron-dental/artifacts/k9vx59de_123.png" alt="Dentakron Logo" className="h-16 w-auto" />
          </Link>
          
          <div className="hidden md:flex space-x-8 items-center h-full">
            <a href="/" className="text-slate-600 hover:text-primary font-medium transition-colors">Ana Sayfa</a>
            
            {/* TEDAVİLER DROPDOWN */}
            {settings.showTreatments !== false && (
            <div 
              className="relative group h-full flex items-center"
              onMouseEnter={() => setDropdownOpen(true)}
              onMouseLeave={() => setDropdownOpen(false)}
            >
              <a href="/#tedaviler" className="text-slate-600 group-hover:text-primary font-medium transition-colors flex items-center gap-1 cursor-pointer h-full">
                Tedaviler <ChevronDown className="w-4 h-4" />
              </a>
              {/* Dropdown Menu */}
              {dropdownOpen && (
                <div className="absolute top-24 left-0 w-72 bg-white rounded-2xl shadow-xl border border-slate-100 py-3 grid grid-cols-1 overflow-hidden z-50">
                  <div className="max-h-[60vh] overflow-y-auto custom-scrollbar">
                    {treatments.map((t) => (
                      <a 
                        key={t.id || t.title} 
                        href={`/tedaviler/${getTreatmentSlug(t)}`}
                        className="block px-6 py-3 text-slate-600 hover:bg-primary/5 hover:text-primary hover:font-bold transition-colors"
                      >
                        {t.title}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
            )}

            {settings.showBeforeAfter !== false && <a href="/#oncesi-sonrasi" className="text-slate-600 hover:text-primary font-medium transition-colors">Öncesi Sonrası</a>}
            {settings.showTeam !== false && <a href="/#hekimlerimiz" className="text-slate-600 hover:text-primary font-medium transition-colors">Hekimlerimiz</a>}
            {settings.showBlogs !== false && <a href="/#blog" className="text-slate-600 hover:text-primary font-medium transition-colors">Blog</a>}
            {settings.showContact !== false && <a href="/#iletisim" className="text-slate-600 hover:text-primary font-medium transition-colors">İletişim</a>}
          </div>

          <div className="hidden md:flex items-center space-x-6">
            <a href={`tel:${phone.replace(/\s+/g, '')}`} className="flex items-center gap-2 text-slate-700 font-semibold hover:text-primary transition-colors">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                <Phone className="w-5 h-5" />
              </div>
              <span className="hidden lg:block">{phone}</span>
            </a>
            <a href="/#randevu" className="bg-primary text-white hover:bg-primary-hover px-6 py-3 rounded-full font-semibold transition-colors" data-testid="nav-randevu-btn">
              Online Randevu
            </a>
          </div>

          <button className="md:hidden p-2 text-slate-600" onClick={() => setIsOpen(!isOpen)} data-testid="mobile-menu-btn">
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="md:hidden bg-white border-t border-slate-100 py-4 px-6 space-y-4 absolute w-full max-h-[80vh] overflow-y-auto z-40">
          <a href="/" className="block text-slate-700 font-medium py-2" onClick={() => setIsOpen(false)}>Ana Sayfa</a>
          
          {settings.showTreatments !== false && (
          <div className="border-y border-slate-50 py-2">
            <div className="text-slate-900 font-bold mb-2">Tedavilerimiz</div>
            <div className="pl-4 space-y-3">
              {treatments.map(t => (
                <a key={t.id || t.title} href={`/tedaviler/${getTreatmentSlug(t)}`} className="block text-slate-600 hover:text-primary" onClick={() => setIsOpen(false)}>
                  • {t.title}
                </a>
              ))}
            </div>
          </div>
          )}

          {settings.showBeforeAfter !== false && <a href="/#oncesi-sonrasi" className="block text-slate-700 font-medium py-2" onClick={() => setIsOpen(false)}>Öncesi Sonrası</a>}
          {settings.showTeam !== false && <a href="/#hekimlerimiz" className="block text-slate-700 font-medium py-2" onClick={() => setIsOpen(false)}>Hekimlerimiz</a>}
          {settings.showBlogs !== false && <a href="/#blog" className="block text-slate-700 font-medium py-2" onClick={() => setIsOpen(false)}>Blog</a>}
          {settings.showContact !== false && <a href="/#iletisim" className="block text-slate-700 font-medium py-2" onClick={() => setIsOpen(false)}>İletişim</a>}

          <div className="pt-4 border-t border-slate-100">
            <a href="/#randevu" onClick={() => setIsOpen(false)} className="block w-full text-center bg-primary text-white py-3 rounded-xl font-semibold">
              Online Randevu
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
